import request from '@teetsh/app/src/utils/request';
import { saveAs } from 'file-saver';
import { useState } from 'react';
import { useSchoolSchoolyear } from '@teetsh/app/src/containers/App/context/SchoolyearSchoolProvider';
import { useTranslation } from 'react-i18next';
import useNotification from 'containers/App/Notifications/useNotification';
import { PdfResponse } from 'types/pdf';
import { buildElevesParams } from 'containers/Eleves/Preview/Sidebar/buildElevesParams';
import { ElevesPDFSettings } from 'types/eleves';

interface Props {
  schoolyearId: string;
  schoolId: string;
  onStart: () => void;
  onFinished: () => void;
  onError: () => void;
  viewId: string;
  viewName: string;
  settings: ElevesPDFSettings;
  lng: string;
}

const fetchEleveAPI = (s3Url: string, name: string) =>
  fetch(s3Url)
    .then(response => response.blob())
    .then(response => {
      saveAs(response, `${name}.pdf`);
    });

export async function downloadEleves(props: Props) {
  const { schoolyearId, schoolId, onStart, onFinished, onError, viewId } =
    props;

  const params = buildElevesParams({
    lng: props.lng,
    settings: props.settings,
  });

  onStart();

  const requestURL = `/schoolyears/${schoolyearId}/schools/${schoolId}/eleves-views/${viewId}/generate?${params.toString()}`;
  try {
    const response: PdfResponse = await request(requestURL, {
      method: 'GET',
    });
    const token = response.pdf_token;
    await fetchEleveAPI(token, props.viewName);
  } catch {
    onError();
  } finally {
    onFinished();
  }

  onFinished();
}

export default function useDownloadEleve() {
  const { schoolyear, school } = useSchoolSchoolyear();
  const { notifyError } = useNotification();
  const { t } = useTranslation(['common']);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  const download = ({
    viewId,
    viewName,
    lng,
    settings,
  }: {
    viewId: string;
    viewName: string;
    lng: string;
    settings: ElevesPDFSettings;
  }) =>
    downloadEleves({
      schoolyearId: schoolyear.id,
      schoolId: school.id,
      onStart: () => setIsDownloading(true),
      onFinished: () => setIsDownloading(false),
      onError: () => notifyError({ title: t('common:messages.genericError') }),
      viewId,
      viewName,
      lng,
      settings,
    });

  return {
    download,
    isDownloading,
  };
}
